/**
 * Shows the `data-combo` block matching the selected audience, on the product pages that tell two
 * stories (signing, identification).
 *
 * Both pages ship the organisation and the end-user content and hide one of them, the same
 * mechanism the certificates page uses for its filter. This lives here rather than in each page's
 * inline script so the two cannot drift apart.
 */
import { setProductAudience, subscribeProductAudience } from '@/stores/app.store'
import { parseProductAudience, DEFAULT_PRODUCT_AUDIENCE } from '@/lib/product-audience'

/**
 * Wires the blocks up for the document as it stands now, and returns the teardown.
 *
 * Call it on `astro:page-load` (initial load and every client-side navigation): bundled module
 * scripts are not re-executed on navigation, so the swapped-in DOM has to be re-queried.
 */
export function setupAudienceBlocks(): () => void {
  const blocks = Array.from(document.querySelectorAll<HTMLElement>('[data-combo]'))
  if (blocks.length === 0) return () => {}

  const apply = (audience: string) => {
    // An audience without its own block (e.g. a hand-typed param) falls back to the default.
    const hasMatch = blocks.some(block => block.dataset.combo === audience)
    const active = hasMatch ? audience : DEFAULT_PRODUCT_AUDIENCE
    blocks.forEach(block => {
      block.hidden = block.dataset.combo !== active
    })
  }

  // The query string — not the store — is the source of truth on (re)entry: the store is a module
  // singleton that can still hold the audience from an earlier visit to this page, or from the
  // other page that uses this switch. This runs before the tabs hydrate, so a linked-to variant
  // shows without a flash of the default one. Later changes (including back/forward) arrive
  // through the store.
  const fromUrl = parseProductAudience(window.location.search)
  setProductAudience(fromUrl)
  apply(fromUrl)

  return subscribeProductAudience(apply)
}
